import React from 'react';
import { DashboardStageProps } from '../../types';
import styles from './dashboard-stage.module.css';

const DashboardStage: React.FC<DashboardStageProps> = ({ stage, isActive, onNext }) => {
  const renderStageContent = () => {
    switch (stage) {
      case 1:
        return (
          <>
            <div className={styles.dashboardHeader}>
              <div className={styles.dashboardDot}></div>
              <div className={styles.dashboardDot}></div>
              <div className={styles.dashboardDot}></div>
            </div>
            
            <div className={styles.portfolioSummary}>
              <div className={styles.portfolioValueCard}>
                <div className={styles.portfolioValueLabel}>Portfolio Value</div>
                <div className={styles.portfolioValueAmount}>$71,820</div>
                <div className={styles.portfolioValueChange}>Today: +$2,847</div>
                <div className={styles.portfolioValuePercent}>+4.1%</div>
                <div className={styles.portfolioValueTotal}>Total Gains: +$6,447</div>
                <div className={styles.portfolioValueTotal}>+9.9%</div>
              </div>
              
              <div className={styles.portfolioHoldings}>
                <div className={styles.portfolioHoldingsLabel}>Portfolio Holdings</div>
                <table className={styles.portfolioTable}>
                  <thead>
                    <tr>
                      <th>Symbol</th>
                      <th>Qty</th>
                      <th>Price</th>
                      <th className="mobile-hidden">Day Chg</th>
                      <th className="mobile-hidden">Total Gain</th>
                      <th>Mkt Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className={styles.ticker}>AAPL</td>
                      <td>127</td>
                      <td>$184.92</td>
                      <td className={`${styles.positive} mobile-hidden`}>+2.1%</td>
                      <td className={`${styles.positive} mobile-hidden`}>+$3,247</td>
                      <td>$23,485</td>
                    </tr>
                    <tr>
                      <td className={styles.ticker}>MSFT</td>
                      <td>47</td>
                      <td>$418.75</td>
                      <td className={`${styles.neutral} mobile-hidden`}>0.0%</td>
                      <td className={`${styles.positive} mobile-hidden`}>+$1,891</td>
                      <td>$19,681</td>
                    </tr>
                    <tr>
                      <td className={styles.ticker}>GOOGL</td>
                      <td>91</td>
                      <td>$175.23</td>
                      <td className={`${styles.negative} mobile-hidden`}>-1.2%</td>
                      <td className={`${styles.negative} mobile-hidden`}>-$847</td>
                      <td>$15,946</td>
                    </tr>
                    <tr>
                      <td className={styles.ticker}>VTI</td>
                      <td>398</td>
                      <td>$287.41</td>
                      <td className={`${styles.positive} mobile-hidden`}>+0.8%</td>
                      <td className={`${styles.positive} mobile-hidden`}>+$2,156</td>
                      <td>$12,708</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '16px' }}>
              <div className={`${styles.clickable} ${styles.riskScoreContainer}`} onClick={onNext}>
                <div className={styles.clickHint}>👆</div>
                <div className={styles.riskScoreLabel}>Risk Score</div>
                <div className={styles.riskScoreValue}>18</div>
                <div className={styles.riskScoreChange}>-2 today</div>
              </div>
              
              <div className={styles.sectorExposure}>
                <div className={styles.sectorExposureLabel}>Sector Exposure</div>
                <div className={styles.sectorExposureGrid}>
                  <div className={styles.sectorCard}>
                    <div className={styles.sectorCardTitle}>🖥️ Technology</div>
                    <div className={styles.sectorCardSubtitle}>AAPL, MSFT, GOOGL</div>
                    <div className={styles.sectorCardPercent}>57.3% of portfolio</div>
                    <div className={styles.sectorCardGains}>+8.2% total gains</div>
                  </div>
                  
                  <div className={`${styles.sectorCard} ${styles.broadMarket}`}>
                    <div className={styles.sectorCardTitle}>📊 Broad Market</div>
                    <div className={styles.sectorCardSubtitle}>VTI</div>
                    <div className={styles.sectorCardPercent}>12.1% of portfolio</div>
                    <div className={styles.sectorCardGains}>+4.1% total gains</div>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.additionalInfo}>
              <div className={styles.infoCard}>
                <div className={styles.infoCardTitle}>📈 Today's Impact</div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>🟢 AAPL:</span>
                  <span className={styles.infoValue}>+$3,247</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>🔴 GOOGL:</span>
                  <span className={`${styles.infoValue} ${styles.negative}`}>-$847</span>
                </div>
              </div>
              
              <div className={styles.infoCard}>
                <div className={styles.infoCardTitle}>📅 Next Events</div>
                <div style={{ fontSize: '11px', color: 'var(--platinum-silver)', marginBottom: '4px' }}>• AAPL Earnings: Jan 28</div>
                <div style={{ fontSize: '11px', color: 'var(--platinum-silver)', marginBottom: '4px' }}>• Fed Meeting: Feb 1</div>
                <div style={{ fontSize: '11px', color: 'var(--platinum-silver)' }}>• Portfolio Review: Weekly</div>
              </div>
            </div>
          </>
        );
      
      case 2:
        return (
          <>
            <div className={styles.riskIntelligence}>
              <div className={styles.riskIntelligenceLabel}>Your Daily Portfolio Risk Score</div>
              <div className={styles.riskIntelligenceScore}>
                <div className={styles.riskIntelligenceValue}>18</div>
                <div className={styles.riskIntelligenceChange}>-2</div>
              </div>
            </div>
            
            <div className={styles.riskSummary}>
              <div className={styles.riskSummaryCard}>
                <div className={styles.riskSummaryTitle}>📊 Market Sentiment</div>
                <div className={styles.riskSummaryValue}>Bullish</div>
                <div className={styles.riskSummaryChange}>+3 from yesterday</div>
              </div>
              
              <div className={styles.riskSummaryCard}>
                <div className={styles.riskSummaryTitle}>⚡ Volatility</div>
                <div className={styles.riskSummaryValue}>Low</div>
                <div className={styles.riskSummaryChange}>-15% this week</div>
              </div>
            </div>
            
            <div className={styles.riskItems}>
              <div className={`${styles.portfolioItem} ${styles.clickable}`} onClick={onNext}>
                <div className={styles.clickHint}>👆</div>
                <span className={styles.ticker}>AAPL</span>
                <span className={`${styles.portfolioItemText} ${styles.positive}`}>Apple posted stable earnings with record iPhone sales in India, signaling durable global demand. <strong>-2</strong></span>
              </div>
              <div className={styles.portfolioItem}>
                <span className={styles.ticker}>MSFT</span>
                <span className={`${styles.portfolioItemText} ${styles.neutral}`}>Microsoft's stock remained steady after expected earnings and no major guidance revisions. <strong>0</strong></span>
              </div>
              <div className={styles.portfolioItem}>
                <span className={styles.ticker}>GOOGL</span>
                <span className={`${styles.portfolioItemText} ${styles.negative}`}>Google's ad revenue missed slightly, raising mild concerns despite solid Cloud performance. <strong>+2</strong></span>
              </div>
              <div className={styles.portfolioItem}>
                <span className={styles.ticker}>VTI</span>
                <span className={`${styles.portfolioItemText} ${styles.positive}`}>Broad U.S. market saw low volatility amid soft-landing economic outlook and declining inflation metrics. <strong>-2</strong></span>
              </div>
            </div>
            
            <div className={styles.marketInsights}>
              <div className={styles.marketInsightsTitle}>🔍 Market Insights</div>
              <div className={styles.marketInsightsGrid}>
                <div className={styles.marketInsightCard}>
                  <div className={styles.marketInsightIcon}>📈</div>
                  <div className={styles.marketInsightText}>Tech sector showing resilience despite macro headwinds</div>
                </div>
                <div className={styles.marketInsightCard}>
                  <div className={styles.marketInsightIcon}>💰</div>
                  <div className={styles.marketInsightText}>Fed policy remains accommodative for growth stocks</div>
                </div>
              </div>
            </div>
          </>
        );
      
      case 3:
        return (
          <>
            <div className={styles.deepDiveHeader}>
              <div className={styles.deepDiveTitle}>AAPL Deep Dive</div>
              <div className={styles.deepDiveSubtitle}>23.4% of portfolio • Risk Impact: -2</div>
            </div>
            
            <div className={styles.deepDiveInsights}>
              <div className={styles.deepDiveInsightTitle}>📈 Why This Matters</div>
              <div className={styles.deepDiveInsightText}>
                India represents Apple's fastest-growing major market. Record sales suggest resilient demand despite economic headwinds.
              </div>
            </div>

            <div className={`${styles.deepDiveInsights} ${styles.secondary}`}>
              <div className={`${styles.deepDiveInsightTitle} ${styles.secondary}`}>🎯 What This Means for You</div>
              <div className={styles.deepDiveInsightText}>
                With Apple at 23.4% of your portfolio, this earnings beat directly strengthens your largest position and supports long-term growth.
              </div>
            </div>

            <div className={styles.deepDiveSuggestions}>
              <div className={styles.deepDiveSuggestionsTitle}>💡 AI Analysis</div>
              
              <div className={styles.suggestionItem}>
                <div className={styles.deepDiveSuggestionTitle}>Position Strength</div>
                <div className={styles.deepDiveSuggestionText}>Earnings beat validates your Apple allocation. Analysis suggests holding through next quarter.</div>
              </div>
              
              <div className={styles.suggestionItem}>
                <div className={styles.deepDiveSuggestionTitle}>Watch Next</div>
                <div className={styles.deepDiveSuggestionText}>Monitor China demand trends and Vision Pro adoption rates in Q1.</div>
              </div>
              
              <div className={styles.suggestionItem}>
                <div className={styles.deepDiveSuggestionTitle}>Risk Analysis</div>
                <div className={styles.deepDiveSuggestionText}>At 23.4% allocation, analysis indicates rebalancing may be needed if Apple exceeds 25% of portfolio.</div>
              </div>
            </div>
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className={`${styles.dashboardStage} ${isActive ? styles.active : ''}`}>
      {renderStageContent()}
    </div>
  );
};

export default DashboardStage; 